import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import SidePanel from 'src/components/sidePanel/SidePanel';
import { badgePayStationLogOptions, badgeResolvedBooleanOptions } from 'src/features/cards/utils/constants';
import { useListAllParkings } from 'src/features/payStations/api/useListAllParkings';
import { useListAllPayStationLogs } from 'src/features/payStations/api/useListAllPayStationLogs';
import { useListPayStations } from 'src/features/payStations/api/useListPaystations';
import LogDetail from 'src/features/payStations/components/LogDetail';
import payStationsMessages from 'src/features/payStations/utils/messages';
import useFilters from 'src/hooks/useFilters';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useRowSelection from 'src/registry/hooks/useRowSelection';

import type { PayStationLog } from 'src/features/payStations/types';

const ALL_LOGS_PANEL_NAME = 'all-logs-panel-panel';

const AllLogsTable = () => {
    const { formatMessage } = useIntl();

    const { clearRowSelection, ...rowSelection } = useRowSelection({ sidePanelName: ALL_LOGS_PANEL_NAME });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'created_at', desc: true }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { data, isLoading, isError } = useListAllPayStationLogs({
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });
    const { data: parkingsData } = useListAllParkings({});
    const { data: payStationsData } = useListPayStations({});

    const columnHelper = createColumnHelper<PayStationLog>();

    const columns = [
        columnHelper.accessor('device.identifier', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableColumnFilter: true,
            id: 'device',
            meta: {
                title: formatMessage(payStationsMessages.device),
                options: payStationsData?.results.map((payStation) => ({
                    value: payStation.id,
                    label: payStation.identifier,
                })),
            },
        }),
        columnHelper.accessor('device.parking.name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            id: 'device__parking',
            meta: {
                title: formatMessage(payStationsMessages.parking),
                options: parkingsData?.results.map((parking) => ({ value: parking.id, label: parking.name })),
            },
        }),
        columnHelper.accessor('level', {
            header: DataTableColumnHeader,
            cell: (props) => <CellBadge badgeOptions={badgePayStationLogOptions} {...props} />,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(payStationsMessages.typeLog),
                badgeOptions: badgePayStationLogOptions,
            },
        }),
        columnHelper.accessor('created_at', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableSorting: true,
            enableColumnFilter: true,
            enableHiding: true,
            meta: {
                title: formatMessage(payStationsMessages.date),
                inputType: INPUT_TYPE.DATE,
            },
        }),
        columnHelper.accessor('message', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(payStationsMessages.message),
                inputType: INPUT_TYPE.TEXT,
            },
        }),
        columnHelper.accessor('resolved', {
            header: DataTableColumnHeader,
            cell: CellBoolean,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(payStationsMessages.errorResolved),
                badgeOptions: badgeResolvedBooleanOptions,
            },
        }),
        columnHelper.accessor('data.activePayment', {
            header: DataTableColumnHeader,
            cell: CellBoolean,
            enableSorting: false,
            enableHiding: true,
            enableColumnFilter: false,
            meta: {
                title: formatMessage(payStationsMessages.hasActivePayment),
            },
        }),
    ];

    const selectedRowData = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelection.rowSelectionState)[0];

        if (!data || !firstSelected) return;
        return data.results.find((log) => log.id === firstSelected);
    }, [data, rowSelection.rowSelectionState]);
    return (
        <>
            <DataTable
                columns={columns}
                data={data}
                pagination={pagination}
                sorting={sorting}
                filters={filters}
                search={search}
                semantics={{
                    title: formatMessage(payStationsMessages.allLogs),
                    entryName: formatMessage(payStationsMessages.log),
                    entryNames: formatMessage(payStationsMessages.logs),
                    sidePanelName: ALL_LOGS_PANEL_NAME,
                }}
                isError={isError}
                isLoading={isLoading}
                rowSelection={rowSelection}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
            />
            <SidePanel onClose={clearRowSelection} name={ALL_LOGS_PANEL_NAME}>
                <LogDetail payStationLog={selectedRowData} />
            </SidePanel>
        </>
    );
};

export default AllLogsTable;
